import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppShell } from "@/components/layout/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertTriangle,
  Banknote,
  ChevronDown,
  FileText,
  Send,
  Sparkles,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useGastosHuerfanos,
  useAssignGastosToEmbarque,
  type GastoHuerfanoRow,
} from "@/hooks/useGastosHuerfanos";
import { EmbarqueCombobox } from "@/components/admin/EmbarqueCombobox";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────── */
/* Helpers                                                             */
/* ──────────────────────────────────────────────────────────────────── */

const fmtCOP = (n: number | null | undefined) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }).format(n);

const fmtCurrency = (n: number, currency: string) => {
  try {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "COP" ? 0 : 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

interface GroupedDoc {
  documento_id: string | null;
  no_documento: string | null;
  tipo_documento: string | null;
  emisor: string | null;
  fecha: string | null;
  gastos: GastoHuerfanoRow[];
  total_cop: number | null; // null si algún gasto no tiene tasa
  has_missing_trm: boolean;
}

function groupByDocumento(rows: GastoHuerfanoRow[]): GroupedDoc[] {
  const map = new Map<string, GroupedDoc>();
  for (const r of rows) {
    const key = r.documento_id ?? `__no_doc__${r.gasto_id}`;
    if (!map.has(key)) {
      map.set(key, {
        documento_id: r.documento_id,
        no_documento: r.no_documento,
        tipo_documento: r.tipo_documento,
        emisor: r.emisor,
        fecha: r.documento_fecha,
        gastos: [],
        total_cop: 0,
        has_missing_trm: false,
      });
    }
    const g = map.get(key)!;
    g.gastos.push(r);
    if (r.monto_cop == null) {
      g.has_missing_trm = true;
      g.total_cop = null;
    } else if (g.total_cop != null) {
      g.total_cop += Number(r.monto_cop);
    }
  }
  return Array.from(map.values());
}

/* ──────────────────────────────────────────────────────────────────── */
/* Page                                                                */
/* ──────────────────────────────────────────────────────────────────── */

export default function AdminGastosPendientes() {
  const { userRole, loading } = useAuth();
  const { data: rows = [], isLoading, isError } = useGastosHuerfanos();
  const assignMut = useAssignGastosToEmbarque();

  const [pendingGroup, setPendingGroup] = useState<GroupedDoc | null>(null);
  const [pendingEmbarqueId, setPendingEmbarqueId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const grouped = useMemo(() => groupByDocumento(rows), [rows]);

  const totals = useMemo(() => {
    let sumCop = 0;
    let missingCount = 0;
    for (const r of rows) {
      if (r.monto_cop == null) missingCount += 1;
      else sumCop += Number(r.monto_cop);
    }
    return {
      totalCop: sumCop,
      missingCount,
      gastosCount: rows.length,
      docsCount: grouped.length,
    };
  }, [rows, grouped]);

  // ── Permisos: solo OWNER/ADMIN ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Cargando…</p>
      </div>
    );
  }
  if (!userRole || !["OWNER", "ADMIN"].includes(userRole)) {
    return <Navigate to="/dashboard/resumen" replace />;
  }

  const handleRequestAssign = (group: GroupedDoc) => {
    if (!pendingEmbarqueId) {
      toast.error("Selecciona un embarque destino primero.");
      return;
    }
    setPendingGroup(group);
    setConfirmOpen(true);
  };

  const handleConfirmAssign = async () => {
    if (!pendingGroup || !pendingEmbarqueId) return;
    const ids = pendingGroup.gastos.map((g) => g.gasto_id);
    try {
      const res = await assignMut.mutateAsync({
        gasto_ids: ids,
        embarque_id: pendingEmbarqueId,
      });
      toast.success(`${res.gastos_asignados} gastos asignados al embarque.`, {
        description: pendingGroup.no_documento
          ? `Factura ${pendingGroup.no_documento}`
          : undefined,
      });
      setConfirmOpen(false);
      setPendingGroup(null);
    } catch (err: any) {
      toast.error("No se pudo asignar", {
        description: err?.message ?? "Error desconocido",
      });
    }
  };

  return (
    <AppShell>
      <main className="flex-1 flex flex-col">
          {/* Header */}
          <header className="h-14 border-b border-border flex items-center px-4 gap-3 bg-card sticky top-0 z-20">
            <SidebarTrigger />
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">Gastos Pendientes</h1>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                Centro de Mando CFO
              </Badge>
            </div>
          </header>

          <div className="flex-1 p-6 space-y-6 max-w-[1400px] w-full mx-auto">
            {/* KPI Ribbon */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    <Banknote className="h-3.5 w-3.5" />
                    Total en Limbo (COP)
                  </div>
                  <div className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                    {fmtCOP(totals.totalCop)}
                  </div>
                  {totals.missingCount > 0 && (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3" />
                      {totals.missingCount} gastos requieren carga de TRM
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    <FileText className="h-3.5 w-3.5" />
                    Documentos pendientes
                  </div>
                  <div className="text-2xl font-bold tabular-nums">{totals.docsCount}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Facturas con gastos sin asignar
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    <Wallet className="h-3.5 w-3.5" />
                    Total de Gastos
                  </div>
                  <div className="text-2xl font-bold tabular-nums">{totals.gastosCount}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Líneas individuales de gasto
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Combobox de embarque destino */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Send className="h-4 w-4 text-primary" />
                  Embarque destino
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-w-xl">
                  <EmbarqueCombobox
                    value={pendingEmbarqueId}
                    onChange={setPendingEmbarqueId}
                    placeholder="Selecciona un embarque abierto…"
                  />
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Solo se muestran embarques abiertos (no liquidados). El destino se aplicará a la
                    factura que asignes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tabla agrupada por documento */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Gastos huérfanos agrupados por factura</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
                ) : isError ? (
                  <div className="p-8 text-center text-sm text-destructive">
                    Error al cargar los gastos.
                  </div>
                ) : grouped.length === 0 ? (
                  <div className="p-12 text-center">
                    <Wallet className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No hay gastos pendientes de asignación.
                    </p>
                    <p className="text-[11px] text-muted-foreground/70 mt-1">
                      Cuando la IA extraiga nuevos gastos de facturas, aparecerán aquí.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {grouped.map((g, idx) => (
                      <GroupRow
                        key={g.documento_id ?? `noDoc-${idx}`}
                        group={g}
                        canAssign={!!pendingEmbarqueId && !assignMut.isPending}
                        onAssign={() => handleRequestAssign(g)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>

      {/* Confirmación */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar asignación de gastos</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a asignar <strong>{pendingGroup?.gastos.length}</strong> gastos
              {pendingGroup?.no_documento ? (
                <>
                  {" "}
                  de la factura <strong>{pendingGroup.no_documento}</strong>
                </>
              ) : null}{" "}
              al embarque seleccionado. Esta acción es atómica y no se puede deshacer desde aquí.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={assignMut.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAssign}
              disabled={assignMut.isPending}
            >
              {assignMut.isPending ? "Asignando…" : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}

/* ──────────────────────────────────────────────────────────────────── */
/* Group row                                                           */
/* ──────────────────────────────────────────────────────────────────── */

function GroupRow({
  group,
  canAssign,
  onAssign,
}: {
  group: GroupedDoc;
  canAssign: boolean;
  onAssign: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                open ? "rotate-180" : "rotate-0"
              )}
            />
          </Button>
        </CollapsibleTrigger>

        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate">
              {group.no_documento ?? "Sin documento"}
            </span>
            {group.tipo_documento && (
              <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                {group.tipo_documento}
              </Badge>
            )}
            <Badge
              variant="secondary"
              className="text-[10px] h-4 px-1.5 bg-primary/10 text-primary border-primary/20"
            >
              <Sparkles className="h-2.5 w-2.5 mr-0.5" />
              IA
            </Badge>
            {group.has_missing_trm && (
              <Badge
                variant="outline"
                className="text-[10px] h-4 px-1.5 border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10"
              >
                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                Sin TRM
              </Badge>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {group.emisor ?? "—"} ·{" "}
            {group.gastos.length} gasto{group.gastos.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="text-right shrink-0">
          <div className="text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            {fmtCOP(group.total_cop)}
          </div>
          <div className="text-[10px] text-muted-foreground">total COP</div>
        </div>

        <Button
          size="sm"
          onClick={onAssign}
          disabled={!canAssign}
          className="shrink-0"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          Asignar
        </Button>
      </div>

      <CollapsibleContent>
        <div className="bg-muted/30 px-4 py-2 border-t border-border">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="text-left font-medium py-1.5">Concepto</th>
                <th className="text-left font-medium py-1.5">Base</th>
                <th className="text-right font-medium py-1.5">Monto</th>
                <th className="text-right font-medium py-1.5">Tasa</th>
                <th className="text-right font-medium py-1.5">COP</th>
              </tr>
            </thead>
            <tbody>
              {group.gastos.map((g) => (
                <tr key={g.gasto_id} className="border-t border-border/50">
                  <td className="py-1.5">{g.concepto}</td>
                  <td className="py-1.5">
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                      {g.base_prorrateo}
                    </Badge>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {fmtCurrency(Number(g.monto), g.moneda)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {g.tasa_aplicada == null ? (
                      <span className="text-amber-600 dark:text-amber-400">—</span>
                    ) : (
                      <>
                        {Number(g.tasa_aplicada).toLocaleString("es-CO", {
                          maximumFractionDigits: 2,
                        })}
                        <span className="text-[9px] ml-1 opacity-70">
                          {g.tasa_fuente === "TRM_PAGO"
                            ? "pago"
                            : g.tasa_fuente === "TRM_HISTORICO"
                            ? "hist."
                            : g.tasa_fuente === "NATIVE_COP"
                            ? "nat."
                            : "—"}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums font-medium text-emerald-600 dark:text-emerald-400">
                    {fmtCOP(g.monto_cop)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
